import { useState, useEffect } from "react";
import axios from "../lib/api";
import {
  Bell,
  Plus,
  Trash2,
  Edit2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const metrics = [
  { value: "roas", label: "ROAS", unit: "x" },
  { value: "cpa", label: "CPA", unit: "€" },
  { value: "ctr", label: "CTR", unit: "%" },
  { value: "conversions", label: "Conversiones", unit: "" },
  { value: "spend", label: "Gasto", unit: "€" },
  { value: "spend_percentage", label: "% Gasto/Ingresos", unit: "%" }
];

const conditions = [
  { value: "above", label: "Mayor que", icon: TrendingUp },
  { value: "below", label: "Menor que", icon: TrendingDown },
  { value: "equals", label: "Igual a", icon: CheckCircle2 }
];

const platforms = [
  { value: "all", label: "Todas las plataformas" },
  { value: "google_ads", label: "Google Ads" },
  { value: "meta_ads", label: "Meta Ads" },
  { value: "tiktok_ads", label: "TikTok Ads" }
];

const AlertForm = ({ alert, onSubmit, onClose }) => {
  const [formData, setFormData] = useState(alert || {
    name: "",
    metric: "roas",
    condition: "below",
    threshold: 0,
    platform: "all",
    enabled: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        platform: formData.platform === "all" ? null : formData.platform
      });
      onClose();
    } catch (error) {
      toast.error("Error al guardar la alerta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label className="text-slate-700">Nombre de la alerta</Label>
        <Input
          placeholder="Ej: ROAS bajo en Meta"
          className="border-slate-200 focus:border-red-500 focus:ring-red-500"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="alert-name-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-700">Métrica</Label>
          <Select
            value={formData.metric}
            onValueChange={(value) => setFormData({ ...formData, metric: value })}
          >
            <SelectTrigger className="border-slate-200" data-testid="alert-metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {metrics.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700">Condición</Label>
          <Select
            value={formData.condition}
            onValueChange={(value) => setFormData({ ...formData, condition: value })}
          >
            <SelectTrigger className="border-slate-200" data-testid="alert-condition-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {conditions.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-700">Umbral</Label>
        <Input
          type="number"
          step="0.01"
          placeholder="Valor del umbral"
          className="border-slate-200 focus:border-red-500 focus:ring-red-500"
          value={formData.threshold}
          onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) })}
          required
          data-testid="alert-threshold-input"
        />
        <p className="text-xs text-slate-500">
          {metrics.find(m => m.value === formData.metric)?.unit && 
            `Unidad: ${metrics.find(m => m.value === formData.metric)?.unit}`}
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-700">Plataforma</Label>
        <Select
          value={formData.platform || "all"}
          onValueChange={(value) => setFormData({ ...formData, platform: value })}
        >
          <SelectTrigger className="border-slate-200" data-testid="alert-platform-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {platforms.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-slate-700">Alerta activa</Label>
        <Switch
          checked={formData.enabled}
          onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
          data-testid="alert-enabled-switch"
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-red-600 hover:bg-red-700 text-white btn-press"
        disabled={loading}
        data-testid="save-alert-btn"
      >
        {loading ? "Guardando..." : alert ? "Actualizar Alerta" : "Crear Alerta"}
      </Button>
    </form>
  );
};

const AlertCard = ({ alert, onEdit, onDelete, onToggle }) => {
  const metric = metrics.find(m => m.value === alert.metric);
  const condition = conditions.find(c => c.value === alert.condition);
  const ConditionIcon = condition?.icon || AlertTriangle;

  return (
    <Card className={`bg-white border ${alert.enabled ? 'border-slate-200' : 'border-slate-100 opacity-60'} hover:shadow-md transition-all`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alert.enabled ? 'bg-red-100' : 'bg-slate-100'}`}>
              <Bell className={`w-5 h-5 ${alert.enabled ? 'text-red-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">{alert.name}</h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                <ConditionIcon className="w-4 h-4" />
                <span>{metric?.label} {condition?.label.toLowerCase()} {alert.threshold}{metric?.unit}</span>
              </div>
              {alert.platform && (
                <Badge variant="secondary" className="mt-2 bg-slate-100 text-slate-600">
                  {platforms.find(p => p.value === alert.platform)?.label}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={alert.enabled}
              onCheckedChange={() => onToggle(alert)}
              data-testid={`toggle-alert-${alert.alert_id}`}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(alert)}
              data-testid={`edit-alert-${alert.alert_id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(alert.alert_id)}
              data-testid={`delete-alert-${alert.alert_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/alerts`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    const token = localStorage.getItem("auth_token");
    await axios.post(`${API}/alerts`, data, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    toast.success("Alerta creada correctamente");
    fetchAlerts();
  };

  const handleUpdate = async (data) => {
    const token = localStorage.getItem("auth_token");
    await axios.put(`${API}/alerts/${editingAlert.alert_id}`, data, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    toast.success("Alerta actualizada");
    fetchAlerts();
  };

  const handleDelete = async (alertId) => {
    if (!confirm("¿Estás seguro de eliminar esta alerta?")) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/alerts/${alertId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Alerta eliminada");
      fetchAlerts();
    } catch (error) {
      toast.error("Error al eliminar la alerta");
    }
  };

  const handleToggle = async (alert) => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(
        `${API}/alerts/${alert.alert_id}`,
        { ...alert, enabled: !alert.enabled },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      fetchAlerts();
    } catch (error) {
      toast.error("Error al cambiar el estado de la alerta");
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAlert(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Alertas</h1>
            <p className="text-slate-600">Configura notificaciones para tus KPIs críticos</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white btn-press"
                onClick={() => setEditingAlert(null)}
                data-testid="create-alert-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Alerta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {editingAlert ? "Editar Alerta" : "Nueva Alerta"}
                </DialogTitle>
              </DialogHeader>
              <AlertForm
                alert={editingAlert}
                onSubmit={editingAlert ? handleUpdate : handleCreate}
                onClose={handleCloseDialog}
              />
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : alerts.length === 0 ? (
          <Card className="bg-slate-50 border border-slate-200">
            <CardContent className="p-12 text-center">
              <Bell className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-slate-900 mb-2">No tienes alertas configuradas</h3>
              <p className="text-slate-600 mb-6">
                Crea alertas para recibir notificaciones cuando tus KPIs crucen umbrales críticos
              </p>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white btn-press"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Primera Alerta
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.alert_id}
                alert={alert}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}

        {/* Info Box */}
        <Card className="bg-amber-50 border border-amber-100">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-900">Ejemplo de alertas recomendadas</h4>
                <ul className="text-sm text-amber-700 mt-2 space-y-1">
                  <li>• ROAS menor que 2x → Revisar campañas urgentemente</li>
                  <li>• CPA mayor que €50 → Optimizar targeting</li>
                  <li>• % Gasto/Ingresos mayor que 40% → Reducir presupuesto</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Alerts;
